import { CommonHelper } from "../../../../../../react-core/Helpers/CommonHelper";
import { addMonths, addYears } from "../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { toDp } from  "../../../../../../react-core/Utils/Math";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { TransferType } from "../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { CashPayment } from "../../../../../../Wrap/Models/Models/PortfolioAnalysis/Payment/CashPayment";
import { IsaBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/IsaBase";
import { ContributionAndWithdrawalBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ContributionAndWithdrawalBase";
import { PaymentsHelper } from "./PaymentsHelper";
import { TaxYearHelper } from "./TaxYearHelper";
import { TransferHelper } from "./TransferHelper";

export class IsaHelper {
    static hasPaymentsTransfer(model: IsaBase): boolean {
        return (
            PaymentsHelper.hasSinglePayments(model) ||
            PaymentsHelper.hasRegularPayments(model) ||
            this.hasTransfers(model)
        );
    }

    static paymentsInTotalForAllowance(model: IsaBase): number {
        //todo add other source
        return (
            PaymentsHelper.singlePaymentsTotal(model) +
            this.addCurrentYearSubscriptionValue(model) +
            PaymentsHelper.regularPaymentsCurrentTaxYearTotal(
                model,
                TaxYearHelper.GetTaxYearEndDate(TaxYearHelper.GetCurrentTaxYear())
            )
        );
    }

    private static addCurrentYearSubscriptionValue(model: IsaBase) {
        const transfers: any = model.IsaTransfers;
        let result = 0;
        if (!transfers || transfers.length === 0) {return result;}

        transfers.forEach((transfer) => {
            //Estimated current year subscription
            if (transfer.CurrentYearSubscriptionValue) {
                result += transfer.CurrentYearSubscriptionValue.Amount;
            }
        });

        return result;
    }

    static transfersTotal(model: IsaBase): number {
        const transfers: any = model.IsaTransfers;
        let result = 0;

        if (!transfers || transfers.length === 0) {return result;}

        transfers.forEach((transfer) => {
            if (
                transfer.TransferType === TransferType.Cash &&
                transfer.CurrentValue &&
                transfer.CurrentValue.Amount > 0
            ) {
                result += transfer.CurrentValue.Amount;
            } else {
                result += TransferHelper.getTransferInvestmentTotal(transfer.Instruments, transfer.ReRegisterValue);
            }
        });

        return result;
    }

    static hasTransfers(model: IsaBase): boolean {
        return model.IsaTransfers && model.IsaTransfers.length > 0;
    }

    static updatedUsed(model: IsaBase, existingRegular: CashPayment[], isCompletedInstruction: boolean): number {
        let used = isCompletedInstruction ? 0 : this.paymentsInTotalForAllowance(model);
        if (model && model.IsaAllowance && model.IsaAllowance.Used && model.IsaAllowance.Used.Amount) {
            used += model.IsaAllowance.Used.Amount;
        }
        if (model && model.IsaAllowance && model.IsaAllowance.Expected && model.IsaAllowance.Expected.Amount) {
            used += model.IsaAllowance.Expected.Amount;
        }
        if (existingRegular?.length > 0) {
            /// remove edit regular payment from expected
            const clientExistingPayment = existingRegular?.find(
                (ele) => ele.BatchId == model?.ClientRegularPayment?.RegularBuyId
            );
            const thirdPartyExistingPayment = existingRegular?.find(
                (ele) => ele.BatchId == model?.ThirdPartyRegularPayment?.RegularBuyId
            );
            const existingExpectedModel: ContributionAndWithdrawalBase = {
                ClientRegularPayment: {
                    Frequency: clientExistingPayment?.Frequency,
                    Money: { Amount: clientExistingPayment?.Amount, Currency: CurrencyCode.GBP },
                    FirstCollectionDate: clientExistingPayment?.FirstDate,
                    NextPaymentDate: clientExistingPayment?.NextDate,
                    ContinueUntil: clientExistingPayment?.FinalDate,
                    IsContinue: CommonHelper.isNullOrEmpty(clientExistingPayment?.FinalDate),
                },
                ThirdPartyRegularPayment: {
                    Frequency: thirdPartyExistingPayment?.Frequency,
                    Money: { Amount: thirdPartyExistingPayment?.Amount, Currency: CurrencyCode.GBP },
                    FirstCollectionDate: thirdPartyExistingPayment?.FirstDate,
                    NextPaymentDate: thirdPartyExistingPayment?.NextDate,
                    ContinueUntil: thirdPartyExistingPayment?.FinalDate,
                    IsContinue: CommonHelper.isNullOrEmpty(thirdPartyExistingPayment?.FinalDate),
                },
            };
            const expectedNeedRemove = PaymentsHelper.regularPaymentsCurrentTaxYearTotal(
                existingExpectedModel,
                TaxYearHelper.GetTaxYearEndDate(TaxYearHelper.GetCurrentTaxYear())
            );
            used = used - expectedNeedRemove;
        }

        return used;
    }

    static validateNextTaxYearIsaAllowance(nextYearTotal: number, nextYearRegularUsed: number): boolean {
        return nextYearRegularUsed <= nextYearTotal;
    }

    static validateIsaAllowance(model: IsaBase, existingRegular: CashPayment[], isCompletedInstruction: boolean): boolean {
        if (model && model.IsaAllowance && model.IsaAllowance.Remaining && model.IsaAllowance.Remaining.Amount) {
            const used = this.updatedUsed(model, existingRegular, isCompletedInstruction);
            return (
                toDp(model.IsaAllowance.Remaining.Amount -
                    toDp((used - (model.IsaAllowance.Used?.Amount ?? 0) - (model.IsaAllowance.Expected?.Amount ?? 0)), 2), 2) >=
                0
            );
        }
        if (model && model.IsaAllowance && model.IsaAllowance.Total && model.IsaAllowance.Total.Amount) {
            const used = this.updatedUsed(model, existingRegular, isCompletedInstruction);
            return model.IsaAllowance.Total.Amount - used >= 0;
        }
        return true;
    }

    static getRegularPaymentFirstCollectionDateInNextTaxYear(
        firstCollectionDate: Date,
        frequent: Frequency,
        nextTaxYearStartDate: Date
    ): Date {
        let gapMonth = 1;
        switch (frequent) {
            case Frequency.Monthly:
                gapMonth = 1;
                break;
            case Frequency.Quarterly:
                gapMonth = 3;
                break;
            case Frequency.HalfYearly:
                gapMonth = 6;
                break;
            case Frequency.Yearly:
                gapMonth = 12;
                break;
        }
        while (firstCollectionDate < nextTaxYearStartDate) {
            firstCollectionDate = addMonths(firstCollectionDate, gapMonth);
        }
        return firstCollectionDate;
    }
    static calculateNextTaxYearISAAllowance(data: IsaBase): number {
        const taxstartDate = addYears(JsonDateParser.toDate(data.IsaAllowance?.TaxYear?.StartDate), 1);
        const taxYearEnd = addYears(JsonDateParser.toDate(data.IsaAllowance?.TaxYear?.EndDate), 1);
        let total = 0;
        if (
            data.ClientRegularPayment != null &&
            data.ClientRegularPayment.Money != null &&
            data.ClientRegularPayment.Frequency != null &&
            data.ClientRegularPayment.FirstCollectionDate != null &&
            (data.ClientRegularPayment.ContinueUntil == null ||
                JsonDateParser.toDate(data.ClientRegularPayment.ContinueUntil) >= taxstartDate)
        ) {
            const startDate = this.getRegularPaymentFirstCollectionDateInNextTaxYear(
                JsonDateParser.toDate(data.ClientRegularPayment.FirstCollectionDate),
                data.ClientRegularPayment.Frequency,
                taxstartDate
            );
            let endDate = new Date();
            if (
                data.ClientRegularPayment.ContinueUntil == null ||
                taxYearEnd < JsonDateParser.toDate(data.ClientRegularPayment.ContinueUntil)
            ) {
                endDate = taxYearEnd;
            } else {
                endDate = JsonDateParser.toDate(data.ClientRegularPayment.ContinueUntil);
            }

            total +=
                data.ClientRegularPayment.Money.Amount *
                PaymentsHelper.getRegularPaymentsCount(startDate, data.ClientRegularPayment.Frequency, endDate);
        }
        if (
            data.ThirdPartyRegularPayment != null &&
            data.ThirdPartyRegularPayment.Money != null &&
            data.ThirdPartyRegularPayment.Frequency != null &&
            data.ThirdPartyRegularPayment.FirstCollectionDate != null &&
            (data.ThirdPartyRegularPayment.ContinueUntil == null ||
                JsonDateParser.toDate(data.ThirdPartyRegularPayment.ContinueUntil) >= taxstartDate)
        ) {
            let endDate = new Date();
            const startDate = this.getRegularPaymentFirstCollectionDateInNextTaxYear(
                JsonDateParser.toDate(data.ThirdPartyRegularPayment.FirstCollectionDate),
                data.ThirdPartyRegularPayment.Frequency,
                taxstartDate
            );
            if (
                data.ThirdPartyRegularPayment.ContinueUntil == null ||
                taxYearEnd < JsonDateParser.toDate(data.ThirdPartyRegularPayment.ContinueUntil)
            ) {
                endDate = taxYearEnd;
            } else {
                endDate = JsonDateParser.toDate(data.ThirdPartyRegularPayment.ContinueUntil);
            }

            total +=
                data.ThirdPartyRegularPayment.Money.Amount *
                PaymentsHelper.getRegularPaymentsCount(startDate, data.ThirdPartyRegularPayment.Frequency, endDate);
        }
        return total;
    }
}
